import React, { useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import useEndpoints from '../../../hooks/useEndpoints';
import { isAxiosError } from 'axios';
import EndpointsTable from '../../../components/EndpointsTable/EndpointsTable';
import { GetEndpointsQuery } from '@hud/portal-dtos/webapp-endpoint.dto';
import Page from '../../../components/Page/Page';
import TimeFrameMenu from '../../../components/TimeFrameMenu/TimeFrameMenu';
import useTimeFrame from '../../../hooks/useTimeFrame';
import useEnvironment from '../../../hooks/useEnvironment';
import EnvironmentMenu from '../../../components/EnvironmentMenu/EnvironmentMenu';
import ActionsContainer from '../../../components/ActionsContainer/ActionsContainer';
import ThemeButton from '../../../components/ThemeButton/ThemeButton';
import useAnalytics from '../../../hooks/useAnalytics';
import { DEFAULT_DEBOUNCE_TIME, DEFAULT_PAGE_SIZE } from '../../../consts';
import { useUserPreferences } from '../../../hooks/useUserPreferences';
import { useQueryParamState } from '../../../hooks/useQueryParamState';
import useSharedTableState, {
  CommonTableQueryParams,
} from '../../../hooks/useSharedTableState';
import { ThresholdFilter } from '@hud/portal-dtos/webapp-common.dto';
import {
  CommonThresholdQueryParams,
  getThresholdFilter,
  ThresholdFilterKey,
} from '../../../utils';
import { Events } from '../../../analytics';

type EndpointsPageQueryParams = CommonTableQueryParams &
  CommonThresholdQueryParams & {
    services: number[];
  };

export default function EndpointsPage() {
  const analytics = useAnalytics();
  const { timeFrame } = useTimeFrame();
  const { environment } = useEnvironment();
  const [totalPages, setTotalPages] = useState<number>(0);
  const { userPreferredServices } = useUserPreferences();

  const [pageState, setPageState, resetPageState] =
    useQueryParamState<EndpointsPageQueryParams>({
      search: '',
      page: 1,
      orderBy: 'normalizedRuns.value',
      desc: true,
      services: [],
      durationAvg: undefined,
      durationAvg_op: undefined,
      normalizedRuns: undefined,
      normalizedRuns_op: undefined,
      errorRate: undefined,
      errorRate_op: undefined,
    });

  const tableState = useSharedTableState({
    pageState,
    setPageState,
    resetPageState,
  });

  useEffect(() => {
    if (pageState.services.length === 0) {
      setPageState({ services: userPreferredServices ?? [] });
    }
  }, [userPreferredServices]);

  const clearAllFilters = () => {
    setPageState({
      services: [],
      durationAvg: undefined,
      durationAvg_op: undefined,
      normalizedRuns: undefined,
      normalizedRuns_op: undefined,
      errorRate: undefined,
      errorRate_op: undefined,
    });
  };

  const handleFilteredServicesChange = (value: number[]) => {
    setPageState({ services: value, page: 1 });
  };

  const handleThresholdFilterChange = (
    key: ThresholdFilterKey,
    value: ThresholdFilter | undefined,
    eventName: keyof Events,
  ) => {
    setPageState({
      [key]: value?.value,
      [`${key}_op`]: value?.operator,
      page: 1,
    });

    analytics.capture(eventName, {
      environment: environment?.name,
      toggle_value: !!value,
      filter_value: value?.value ?? 0,
      filter_operator: value?.operator ?? 'gte',
    });
  };

  const handleFilteredDurationAvgChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'durationAvg',
      value,
      'endpoints_duration_avg_filter_toggled',
    );
  };

  const handleFilteredNormalizedRunsChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'normalizedRuns',
      value,
      'endpoints_normalized_runs_filter_toggled',
    );
  };

  const handleFilteredErrorRateChange = (value: ThresholdFilter | undefined) => {
    handleThresholdFilterChange(
      'errorRate',
      value,
      'endpoints_error_rate_filter_toggled',
    );
  };

  const endpointsQueryParams = useMemo<GetEndpointsQuery>(
    () => ({
      limit: DEFAULT_PAGE_SIZE,
      offset: (tableState.page || 1) * DEFAULT_PAGE_SIZE - DEFAULT_PAGE_SIZE,
      searchTerms: tableState.search.split(' ').filter(Boolean),
      orderBy: tableState.orderBy as GetEndpointsQuery['orderBy'],
      timeFrame,
      environmentName: environment?.name,
      serviceIds: pageState.services,
      durationAvg: getThresholdFilter(pageState.durationAvg, pageState.durationAvg_op),
      normalizedRuns: getThresholdFilter(
        pageState.normalizedRuns,
        pageState.normalizedRuns_op,
      ),
      errorRate: getThresholdFilter(pageState.errorRate, pageState.errorRate_op),
    }),
    [
      tableState.page,
      tableState.search,
      tableState.orderBy,
      timeFrame,
      environment?.name,
      pageState.services,
      pageState.durationAvg,
      pageState.durationAvg_op,
      pageState.normalizedRuns,
      pageState.normalizedRuns_op,
      pageState.errorRate,
      pageState.errorRate_op,
    ],
  );
  const [debouncedEndpointsQueryParams] = useDebouncedValue(
    endpointsQueryParams,
    DEFAULT_DEBOUNCE_TIME,
  );
  const endpointsQuery = useEndpoints(debouncedEndpointsQueryParams);

  useEffect(() => {
    document.title = 'Endpoints • Hud';
  }, []);

  useEffect(() => {
    if (endpointsQuery.isSuccess) {
      setTotalPages(Math.ceil(endpointsQuery.data.countFiltered / DEFAULT_PAGE_SIZE));
    }
  }, [endpointsQuery.data, endpointsQuery.isSuccess]);

  // Analytics
  // useEffect(() => {
  //   if (!environment?.name) return;
  //   analytics.capture('endpoints_viewed', {
  //     environment: environment?.name,
  //   });
  //   if (!initialLoad) {
  //   setInitialLoad(true);
  // } else {
  //   onPaginationChange(1);
  // }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [environment?.name]);

  // useEffect(() => {
  //   if (!environment?.name) return;
  //   analytics.capture('endpoints_searched', {
  //     search: searchValue,
  //     environment: environment?.name,
  //   });
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [debouncedEndpointsQueryParams.searchTerms]);

  // useEffect(() => {
  //   if (endpointsQuery.isSuccess) {
  //     analytics.capture('endpoints_paginated', {
  //       page: pagination.active,
  //       column_name: orderBy?.key ?? '',
  //       column_order: orderBy?.sortOrder ?? 'asc',
  //       search: searchValue,
  //       environment: environment?.name,
  //     });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [pagination.active]);

  // useEffect(() => {
  //   if (endpointsQuery.isSuccess) {
  //     analytics.capture('endpoints_sorted', {
  //       column_name: orderBy?.key ?? '',
  //       column_order: orderBy?.sortOrder ?? 'asc',
  //       environment: environment?.name,
  //     });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [orderBy?.key]);

  if (endpointsQuery.isError) {
    console.log(endpointsQuery.error);
    if (!isAxiosError(endpointsQuery.error)) {
      return (
        <div>
          <div>Something went wrong</div>
        </div>
      );
    }
    const error = endpointsQuery.error.response?.data;
    return <div>{error?.message ?? 'Something went wrong'}</div>;
  }

  return (
    <Page
      header="Endpoints"
      actions={
        <ActionsContainer>
          <EnvironmentMenu />
          <TimeFrameMenu />
          <ThemeButton />
        </ActionsContainer>
      }
    >
      <EndpointsTable
        fullPageTable
        endpoints={endpointsQuery.data?.endpoints ?? []}
        loading={endpointsQuery.isLoading}
        total={endpointsQuery.data?.total}
        filtered={endpointsQuery.data?.countFiltered}
        searchValue={tableState.search}
        onSearchChange={tableState.handleSearchChange}
        sortingState={tableState.sortingState}
        onSortingChange={tableState.handleSortingChange}
        pages={totalPages}
        currentPage={totalPages ? tableState.page : 0}
        onPageChange={tableState.handlePaginationChange}
        filteredServices={pageState.services}
        onFilteredServicesChange={handleFilteredServicesChange}
        filteredDurationAvg={getThresholdFilter(
          pageState.durationAvg,
          pageState.durationAvg_op,
        )}
        onFilteredDurationAvgChange={handleFilteredDurationAvgChange}
        filteredNormalizedRuns={getThresholdFilter(
          pageState.normalizedRuns,
          pageState.normalizedRuns_op,
        )}
        onFilteredNormalizedRunsChange={handleFilteredNormalizedRunsChange}
        filteredErrorRate={getThresholdFilter(
          pageState.errorRate,
          pageState.errorRate_op,
        )}
        onFilteredErrorRateChange={handleFilteredErrorRateChange}
        onClearAllFilters={clearAllFilters}
        timeFrame={timeFrame}
      />
    </Page>
  );
}
